import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
  inject,
} from '@angular/core';
import {
  BoFormsModule,
  FormFieldComponent,
  FieldType,
  FormOptionsDirective,
  FieldPathDirective,
  InputType,
} from '@bo/ng-forms';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxCurrencyDirective } from '@dintecom/ngx-currency';
import { ReactiveFormsModule } from '@angular/forms';
import { DynamicFormDirective } from '../../../directives/dynamic-form-directive/dynamic-form.directive';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { Subscription } from 'rxjs';
import { RecaptchaModule } from 'ng-recaptcha-2';
import { NgbDateAdapter, NgbDateParserFormatter, NgbDateStruct, NgbInputDatepicker } from '@ng-bootstrap/ng-bootstrap';
import { CustomDateParserFormatter } from '../date-range-picker/date-range-picker.component';

/**
 * Bridges ng-bootstrap's internal `NgbDateStruct` model to/from the plain
 * ISO `yyyy-mm-dd` string that `fieldControl` stores (and the backend expects),
 * so switching to `ngbDatepicker` doesn't change the value shape of date fields.
 */
@Injectable()
export class IsoStringDateAdapter extends NgbDateAdapter<string> {
  fromModel(value: string | null): NgbDateStruct | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day ? { year, month, day } : null;
  }

  toModel(date: NgbDateStruct | null): string | null {
    return date ? `${date.year}-${this.pad(date.month)}-${this.pad(date.day)}` : null;
  }

  private pad(value: number): string {
    return value < 10 ? `0${value}` : `${value}`;
  }
}

@Component({
  selector: 'app-form-field',
  imports: [
    BoFormsModule,
    NgClass,
    NgSelectModule,
    NgxCurrencyDirective,
    NgTemplateOutlet,
    ReactiveFormsModule,
    RecaptchaModule,
    DynamicFormDirective,
    TooltipModule,
    NgbInputDatepicker,
  ],
  providers: [
    { provide: NgbDateAdapter, useClass: IsoStringDateAdapter },
    { provide: NgbDateParserFormatter, useClass: CustomDateParserFormatter },
  ],
  templateUrl: './form-field.component.html',
  styles: [
    `
      /* Detect Chrome autofill using CSS animation trick */
      @keyframes onAutoFillStart {
        from {
          /**/
        }
        to {
          /**/
        }
      }

      input:-webkit-autofill {
        animation-name: onAutoFillStart;
        animation-duration: 0.001s;
      }
    `,
  ],
})
export class FieldComponent extends FormFieldComponent implements OnInit, AfterViewInit, OnDestroy {
  protected formOptionsDirective: FormOptionsDirective;
  protected override fieldPathDirective: FieldPathDirective;
  private readonly cdr = inject(ChangeDetectorRef);

  override readonly FieldType = FieldType;
  readonly subscriptions = new Subscription();

  @ViewChild('inputElement', { static: false }) protected inputElement?: ElementRef<HTMLInputElement>;
  @Input() override label!: string;
  @Input() boFieldPath?: string;
  @Input() type?: InputType;
  @Input() placeholder?: string;
  @Input() labelTemplate?: TemplateRef<HTMLElement>;
  @Input() labelClass?: string;
  @Input() inputClass?: string;
  @Input() inputFunction?: (value: string) => string;
  @Input() minDate?: Date = new Date();
  @Input() readonly?: boolean = false;
  @Input() override addCurrencyMask?: boolean = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() override currencyMaskOptions?: any;
  @Output() changeEmitter: EventEmitter<string> = new EventEmitter<string>();

  get fieldType(): FieldType | InputType {
    return this.type ?? this.fieldOption.type ?? FieldType.input;
  }

  protected validityClass = '';
  private isProcessingAutofill = false;

  constructor() {
    const formOptionsDirective = inject(FormOptionsDirective);
    const fieldPathDirective = inject(FieldPathDirective);

    super(formOptionsDirective, fieldPathDirective);

    this.formOptionsDirective = formOptionsDirective;
    this.fieldPathDirective = fieldPathDirective;
  }

  override ngOnInit() {
    // Parent sets fieldControl / fieldOption — must run first.
    super.ngOnInit();

    this.updateValidityClass();

    // statusChanges does not fire on markAsTouched() (used by form submit).
    // fieldControl.events covers value, status, touched, and pristine changes.
    if (this.fieldControl) {
      this.subscriptions.add(
        this.fieldControl.events.subscribe(() => {
          this.updateValidityClass();
          this.cdr.markForCheck();
        }),
      );
    }
  }

  ngAfterViewInit() {
    // Listen for browser autofill events on currency masked fields
    const input = this.inputElement?.nativeElement;
    if (input && this.addCurrencyMask) {
      // Chrome/Safari autofill detection using CSS animation trick
      input.addEventListener('animationstart', (e: AnimationEvent) => {
        if (e.animationName === 'onAutoFillStart') {
          this.handleAutofill();
        }
      });

      // Fallback: also listen for change event (fires when user selects from autofill dropdown)
      input.addEventListener('change', () => {
        this.handleAutofill();
      });
    }
  }

  private handleAutofill() {
    // Temporarily suppress validation during autofill processing
    this.isProcessingAutofill = true;

    // Wait for Chrome to fully populate the input value
    setTimeout(() => {
      const input = this.inputElement?.nativeElement;
      if (!input) {
        this.isProcessingAutofill = false;
        return;
      }

      // Get the autofilled value and parse it to a number
      const autofilledValue = input.value;
      if (autofilledValue) {
        const numericValue = Number.parseFloat(autofilledValue.replaceAll(/[^0-9.-]/g, ''));

        if (!Number.isNaN(numericValue)) {
          // Set the value directly to the form control
          this.fieldControl?.setValue(numericValue, { emitEvent: true });
        }
      }

      // Trigger input event to let ngx-currency format the display
      const inputEvent = new Event('input', { bubbles: true });
      input.dispatchEvent(inputEvent);

      // Re-enable validation after ngx-currency finishes formatting
      setTimeout(() => {
        this.isProcessingAutofill = false;
        this.updateValidityClass();
        this.cdr.detectChanges();
      }, 100);
    }, 150);
  }

  private updateValidityClass() {
    // Don't show validation errors while processing autofill
    // This prevents "field may not be blank" errors during the brief moment
    // when ngx-currency is processing the autofilled value
    if (this.isProcessingAutofill) {
      this.validityClass = '';
      return;
    }

    // Only show validation errors if:
    // 1. The field has been touched (user interacted with it), OR
    // 2. The field is dirty (value changed by user or autofill has been processed)
    const shouldShowErrors = this.fieldControl?.touched || this.fieldControl?.dirty;

    if (shouldShowErrors) {
      this.validityClass = this.getValidityClass() || '';
    } else {
      // Don't show validation errors on pristine/untouched fields
      this.validityClass = '';
    }
  }

  onInput() {
    this.changeEmitter.emit();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
